import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { Header } from '@/ui/components/Header';
import { Footer } from '@/ui/components/Footer';
import { MobilePageHeader } from '@/ui/components/MobilePageHeader';
import { SEO, metaFrom } from '@/ui/components/SEO';
import { Shield, Database, Mail, MapPin, Clock, UserCheck, Trash2, UserCog, Network } from 'lucide-react';

/**
 * Page Politique de Confidentialité — /confidentialite
 * Informations RGPD sur les données collectées, finalités, droits des utilisateurs.
 */
const PrivacyPolicyPage: React.FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const isNative = Capacitor.isNativePlatform();

    // Sections avec icône, titre et contenu (tableaux de paragraphes)
    const sections = [
        {
            icon: <Database className="w-5 h-5" />,
            titleKey: 'privacy.page.dataCollected.title',
            items: [
                'privacy.page.dataCollected.visitorId',
                'privacy.page.dataCollected.ip',
                'privacy.page.dataCollected.pages',
                'privacy.page.dataCollected.email',
                'privacy.page.dataCollected.geolocation',
                'privacy.page.dataCollected.userAgent',
            ],
        },
        {
            icon: <Shield className="w-5 h-5" />,
            titleKey: 'privacy.page.purposes.title',
            items: [
                'privacy.page.purposes.analytics',
                'privacy.page.purposes.newsletter',
                'privacy.page.purposes.contact',
                'privacy.page.purposes.improvement',
            ],
        },
        {
            icon: <Clock className="w-5 h-5" />,
            titleKey: 'privacy.page.retention.title',
            items: [
                'privacy.page.retention.analytics',
                'privacy.page.retention.account',
                'privacy.page.retention.authEvents',
                'privacy.page.retention.email',
                'privacy.page.retention.messages',
                'privacy.page.retention.trialLedger',
                'privacy.page.retention.founderLedger',
            ],
        },
        {
            icon: <MapPin className="w-5 h-5" />,
            titleKey: 'privacy.page.cookies.title',
            items: [
                'privacy.page.cookies.noThirdParty',
                'privacy.page.cookies.anonymous',
                'privacy.page.cookies.authCookie',
                'privacy.page.cookies.localStorage',
                'privacy.page.cookies.audienceId',
                'privacy.page.cookies.legalBasis',
            ],
        },
        {
            icon: <UserCheck className="w-5 h-5" />,
            titleKey: 'privacy.page.rights.title',
            items: [
                'privacy.page.rights.access',
                'privacy.page.rights.rectification',
                'privacy.page.rights.deletion',
                'privacy.page.rights.opposition',
                'privacy.page.rights.portability',
            ],
        },
        {
            icon: <Trash2 className="w-5 h-5" />,
            titleKey: 'privacy.page.exercise.title',
            items: [
                'privacy.page.exercise.howTo',
            ],
        },
        {
            icon: <UserCog className="w-5 h-5" />,
            titleKey: 'privacy.page.sentinelAccount.title',
            items: [
                'privacy.page.sentinelAccount.intro',
                'privacy.page.sentinelAccount.credentials',
                'privacy.page.sentinelAccount.observation',
                'privacy.page.sentinelAccount.projects',
                'privacy.page.sentinelAccount.alerts',
                'privacy.page.sentinelAccount.basis',
                'privacy.page.sentinelAccount.trialLedger',
                'privacy.page.sentinelAccount.founderLedger',
            ],
        },
        {
            icon: <Network className="w-5 h-5" />,
            titleKey: 'privacy.page.subprocessors.title',
            items: [
                'privacy.page.subprocessors.intro',
                'privacy.page.subprocessors.ovh',
                'privacy.page.subprocessors.cloudflare',
                'privacy.page.subprocessors.stripe',
                'privacy.page.subprocessors.google',
                'privacy.page.subprocessors.openmeteo',
                'privacy.page.subprocessors.metno',
                'privacy.page.subprocessors.brevo',
                'privacy.page.subprocessors.firebase',
                'privacy.page.subprocessors.sentry',
                'privacy.page.subprocessors.freeipapi',
            ],
        },
        {
            icon: <Mail className="w-5 h-5" />,
            titleKey: 'privacy.page.thirdParty.title',
            items: [
                'privacy.page.thirdParty.noSale',
                'privacy.page.thirdParty.maplibre',
                'privacy.page.thirdParty.nominatim',
                'privacy.page.thirdParty.googlePlayAge',
            ],
        },
    ];

    return (
        <>
            {/* Titre et intro déjà traduits : pas de clés seo.* dédiées. */}
            <SEO title={t('privacy.page.title')} description={metaFrom(t('privacy.page.intro'))} />
            {isNative
                ? <MobilePageHeader title={t('privacy.page.title')} icon="shield" showBackButton onBack={() => navigate(-1)} />
                : <Header />
            }
            <main className={`min-h-screen bg-[#0f2023] text-white ${isNative ? 'pt-20 pb-24' : 'pt-28 pb-20'}`}>
                <div className="max-w-3xl mx-auto px-6">
                    {/* Titre */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-primary text-sm mb-4">
                            <Shield className="w-4 h-4" />
                            RGPD
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold mb-3">
                            {t('privacy.page.title')}
                        </h1>
                        <p className="text-slate-400 text-sm">
                            {t('privacy.page.lastUpdate')}
                        </p>
                    </div>

                    {/* Introduction */}
                    <p className="text-slate-300 leading-relaxed mb-10 text-sm">
                        {t('privacy.page.intro')}
                    </p>

                    {/* Sections */}
                    <div className="space-y-8">
                        {sections.map((section, idx) => (
                            <section
                                key={idx}
                                className="bg-white/[0.03] border border-white/10 rounded-xl p-6"
                            >
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="text-primary">{section.icon}</div>
                                    <h2 className="text-lg font-semibold">
                                        {t(section.titleKey)}
                                    </h2>
                                </div>
                                <ul className="space-y-2">
                                    {section.items.map((itemKey, i) => (
                                        <li
                                            key={i}
                                            className="text-slate-400 text-sm leading-relaxed flex items-start gap-2"
                                        >
                                            <span className="text-primary/60 mt-1.5">•</span>
                                            <span>{t(itemKey)}</span>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ))}
                    </div>
                </div>
            </main>
            {!isNative && <Footer />}
        </>
    );
};

export default PrivacyPolicyPage;

